import { CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { BaseModal } from "../../BaseModal";
import { CustomButton } from "../../CustomButton";
import { AccountsList } from "../GetMoreContactsForAccountsModal/AccountsList";
import { DotIcon } from "../../../images";
import { setIsNotificationsOpen } from "../../../../features/companySlice";
import { useAppDispatch } from "../../../../hooks";
import {
  accountsApi,
  campaignsApi,
  useGenerateAccountsPlanListMutation,
} from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { ManageSectionsBlock } from "../../ManageSectionsBlock";
import { ManageSectionsModals } from "../../../../types/enums";
import { AccountPlanItem } from "../../../../types/apiTypes";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  analyzedAccounts: AccountPlanItem[];
  campaign_id: string;
};

const ListItem = ({ text }: { text: string }) => {
  return (
    <Stack flexDirection="row" alignItems="flex-start" paddingLeft="4px">
      <DotIcon
        width="20px"
        height="20px"
        color="#464646"
        style={{ marginTop: "3px" }}
      />
      <Typography variant="subtitle1" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
};

const textList = [
  "All buying committee is verified and meet your expectations",
  "The search for additional contacts is completed based on gap analysis results",
  'Contact information is updated using "Validate Contacts Data" feature',
];

export const TargetedAccountPlansModal = ({
  isOpen,
  onClose,
  analyzedAccounts,
  campaign_id,
}: Props) => {
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<string[]>([]);
  const [isHide, setIsHide] = useState<boolean>(false);

  const [generateAccountsPlanList, { isLoading }] =
    useGenerateAccountsPlanListMutation();

  useEffect(() => {
    const prefilteredIds = analyzedAccounts.map(({ account_id }) => account_id);

    setSelected((prev) => {
      if (prev.length === 0) {
        return prefilteredIds;
      }

      return prev.filter((id) => prefilteredIds.includes(id));
    });
  }, [analyzedAccounts]);

  const submitHandler = () => {
    generateAccountsPlanList({ campaign_id, account_ids: selected })
      .unwrap()
      .then(() => {
        dispatch(setIsNotificationsOpen(true));
        onClose();
        dispatch(accountsApi.util.invalidateTags(["list-view"]));
        dispatch(campaignsApi.util.invalidateTags(["activities", "summary"]));
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title="Failed to start generating account plans."
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        );
      });
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      title="Get Targeted Account Plans"
      sx={{
        padding: "32px",
        maxWidth: "548px",
        width: "94%",
        visibility: isHide ? "hidden" : "visible",
      }}
      btnBlock={
        <Stack direction="row" gap="8px" paddingTop="16px">
          <CustomButton
            onClick={onClose}
            variant="contained"
            textColor="#4C545B"
            fullWidth
            type="button"
            customColor="#F1F1F1"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={submitHandler}
            variant="contained"
            fullWidth
            type="button"
            customColor="#1C3C6C"
            startIcon={
              isLoading && (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              )
            }
            disabled={isLoading}
          >
            Get Account Plans
          </CustomButton>
        </Stack>
      }
    >
      <Stack gap="16px" paddingBottom="12px">
        <Stack>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight={500}
          >
            Which accounts would you like to create Account Plans for?
          </Typography>
          {analyzedAccounts.length > 0 && (
            <AccountsList
              accounts={analyzedAccounts.map(
                ({ account_id, account_name }) => ({
                  id: account_id,
                  name: account_name,
                  missed: 0,
                }),
              )}
              selectedIds={selected}
              setSelectedIds={setSelected}
              withSelectAll
              withSort={false}
              withHeader={false}
              withMissed={false}
            />
          )}
        </Stack>
        <Stack>
          <Typography variant="subtitle1" color="text.secondary">
            Account Plans can only be generated once.
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Please make sure everything is correct before proceeding:
          </Typography>
        </Stack>
        <Stack>
          {textList.map((text, index) => (
            <ListItem key={index} text={text} />
          ))}
        </Stack>
      </Stack>
      <ManageSectionsBlock
        adjustSection={"Account Plan"}
        manageSections={"Account Plan"}
        hidePrevModalHandler={setIsHide}
        closePrevModalHandler={onClose}
        modal={ManageSectionsModals.account_plan}
      />
    </BaseModal>
  );
};
